import React from 'react';

import cs from 'classnames';
import find from 'lodash/find';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ProcedureStepNumber} from 'app/procedures/components/ProcedureStepNumber';
import {
  StepDecisionTitle,
  StyledStepContentSeparatorDiv,
} from 'app/procedures/components/structures';
import {StyledStepAttachedLi} from 'app/procedures/styles';
import {gt} from 'app/utils/i18n';

import type {AnyStepType, DecisionStepChoiceType} from 'sweet-types';

interface ChoiceWithChangedState extends DecisionStepChoiceType {
  changedState?: string;
}

interface Props {
  choices?: Array<ChoiceWithChangedState>;
  steps: Array<AnyStepType>;
  disabled: boolean;
  userIndicator: React.ReactNode;
  onSetStepChoice: (
    choice: ChoiceWithChangedState,
    checked: boolean
  ) => Promise<any>;
  pickedChoice: null | string;
}

export function CheckableChoices({
  choices,
  steps,
  onSetStepChoice,
  disabled,
  userIndicator,
  pickedChoice,
}: Props): JSX.Element | null {
  return choices == null || choices.length == 0 ? null : (
    <StyledStepContentSeparatorDiv className="choices">
      <StepDecisionTitle />
      <ul className="list-group">
        {choices.map((choice) => {
          const choiceChecked = choice.id === pickedChoice;
          return (
            <LazyTooltip
              key={choice.id}
              placement="top"
              hide={choiceChecked}
              tooltip={gt('Select this answer')}
            >
              <ChoiceDestination
                onClick={(e) => {
                  e.preventDefault();

                  if (!disabled) {
                    onSetStepChoice(choice, !choiceChecked);
                  }
                }}
                choice={choice}
                steps={steps}
                userIndicator={userIndicator}
                isChecked={choiceChecked}
                disabled={disabled}
              />
            </LazyTooltip>
          );
        })}
      </ul>
    </StyledStepContentSeparatorDiv>
  );
}

interface ChoiceDestinationProps {
  steps: Array<AnyStepType>;
  choice: ChoiceWithChangedState;
  isChecked: boolean;
  disabled?: boolean;
  userIndicator?: React.ReactNode;
  onClick?: (e: React.MouseEvent) => unknown;
}

export const ChoiceDestination = React.forwardRef<
  HTMLLIElement,
  ChoiceDestinationProps
>(
  (
    {
      choice,
      steps,
      isChecked,
      disabled = false,
      userIndicator,
      ...other
    }: ChoiceDestinationProps,
    ref
  ) => {
    const step = find(steps, {
      id: choice.dest,
    });
    return (
      <StyledStepAttachedLi
        ref={ref}
        className={cs(
          'list-group-item',
          'list-group-item-action',
          'p-0',
          choice.changedState,
          {
            active: isChecked,
            disabled,
          }
        )}
      >
        <a
          {...other}
          href={`#${choice.dest}`}
          className="py-2 px-4 d-flex align-items-center justify-content-between"
        >
          <div>
            <input
              type="radio"
              style={{
                pointerEvents: 'none',
              }}
              disabled={disabled}
              className="mr-2"
              checked={isChecked}
              readOnly={true}
            />{' '}
            {choice.name}
          </div>
          <div className="d-flex align-items-center">
            {isChecked ? userIndicator : null}

            {step != null ? (
              <ProcedureStepNumber n={step.n} type={step.type} />
            ) : null}
          </div>
        </a>
      </StyledStepAttachedLi>
    );
  }
);
